import { BACKEND_DATE_FORMAT } from 'config/Vars'
import {
  FilterType,
  buildPerformanceInsightsSummaryDatatableFilter,
  usePerformanceInsightsSummaryDatatable,
} from 'generated/resources/datatable.graphql'
import moment from 'moment'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

const DEFAULT_MIN_DATE = moment('1970-01-01')

const MONTH_RANGES = [
  { startMonths: 0, endMonths: 1, label: 'less_than_one_month' },
  { startMonths: 1, endMonths: 3, label: 'one_to_three_months' },
  { startMonths: 3, endMonths: 6, label: 'three_to_six_months' },
  { startMonths: 6, endMonths: 9, label: 'six_to_nine_months' },
  { startMonths: 9, endMonths: 12, label: 'nine_to_twelve_months' },
] as const

export const label = (index: number) => {
  switch (index) {
    case 0:
      return i18n.t('performance_process.analytics.datatable.filters.less_than_one_month')
    case 1:
      return i18n.t('performance_process.analytics.datatable.filters.one_to_three_months')
    case 2:
      return i18n.t('performance_process.analytics.datatable.filters.three_to_six_months')
    case 3:
      return i18n.t('performance_process.analytics.datatable.filters.six_to_nine_months')
    case 4:
      return i18n.t('performance_process.analytics.datatable.filters.nine_to_twelve_months')
    case 15:
      return i18n.t('performance_process.analytics.datatable.filters.more_than_ten')
    default:
      return `${index - 4} ${i18n.t('common.years.other')}`
  }
}

/**
 * Generate date ranges for filtering employees based on their tenure in the company.
 *
 * This function creates an array of date ranges that includes:
 * - Special month ranges (0-1, 1-3, 3-6, 6-9, 9-12 months)
 * - Annual ranges for years 1-10
 * - A final range for "more than 10 years"
 *
 * Each range is formatted as [startDate, endDate] in BACKEND_DATE_FORMAT.
 */
export const generateDateRanges = () => {
  const now = moment()
  const dateRanges: [string, string][] = []

  // Month ranges (0-1, 1-3, 3-6, 6-9, 9-12 months) - mutually exclusive
  // Each range ends 1 day before the next range starts
  let rangeEnd = now.clone()
  MONTH_RANGES.forEach(({ startMonths, endMonths }, index) => {
    const rangeStart = rangeEnd.clone().subtract(endMonths - startMonths, 'months')

    if (index > 0) rangeEnd.subtract(1, 'day')

    dateRanges.push([rangeStart.format(BACKEND_DATE_FORMAT), rangeEnd.format(BACKEND_DATE_FORMAT)])

    rangeEnd = rangeStart
  })

  // Annual ranges (1-10 years)
  rangeEnd = rangeEnd.subtract(1, 'day')
  for (let year = 1; year <= 10; year++) {
    const yearStart = rangeEnd.clone().subtract(1, 'year').add(1, 'day')

    dateRanges.push([yearStart.format(BACKEND_DATE_FORMAT), rangeEnd.format(BACKEND_DATE_FORMAT)])

    // Move to the next year end
    rangeEnd = rangeEnd.subtract(1, 'year')
  }

  // More than 10 years
  dateRanges.push([
    DEFAULT_MIN_DATE.format(BACKEND_DATE_FORMAT),
    rangeEnd.format(BACKEND_DATE_FORMAT),
  ])

  return dateRanges
}

const tenureDateFilter = (source: ReturnType<typeof usePerformanceInsightsSummaryDatatable>) => {
  const options: { label: string; value: string }[] = []

  generateDateRanges().forEach((dates, i) => {
    options.push({
      label: label(i),
      value: dates.join('#'),
    })
  })

  return buildPerformanceInsightsSummaryDatatableFilter(source, {
    title: i18n.t('performance_process.analytics.datatable.filters.tenure'),
    path: 'tenureDate',
    identifier: 'tenureDateFilter',
    type: FilterType.String,
    default: false,
    categories: [
      {
        kind: 'MultipleChoice',
        operation: 'in',
        options,
      },
    ],
    onUpdateValue(value) {
      track(trackingEvents.performanceAnalytics.insightsSummaryFilterApplied, {
        type: 'tenureDateFilter',
        value,
      })
    },
  })
}

export default tenureDateFilter
